import { PropsWithChildren, useState } from "react";
import sickNote from "../../assets/sick-note.jpg";
import workAccidentCertificate from "../../assets/work-accident-certificate.jpg";
import { useMember } from "../../context/memberContext";
import { useTranslations } from "../../hooks/useTranslations";
import { useEmergencyStatus } from "../../http/serviceportalApi";
import Button from "../ui/Button";
import ErrorText from "../ui/ErrorText";
import LoadingSpinner from "../ui/LoadingSpinner";
import Page from "../ui/Page";
import EmergencyModal from "./EmergencyModal";
import styles from "./EmergencyService.module.css";
import PayrollOfficeEmailAddress from "./PayrollOfficeEmailAddress";

const EmergencyService = () => {
  const { i18n } = useTranslations();
  const [isModalOpen, setModalOpen] = useState(false);
  const { data: status, isValidating, error } = useEmergencyStatus();
  const isSuccess = !isValidating && !error;
  const { member } = useMember();
  if (!member) {
    return null;
  }

  const es = i18n.emergencyService;

  return (
    <Page title={es.title} backLinkText={i18n.payrollBackLink}>
      {!member.giottoId ? (
        <div className={styles.status}>{i18n.configIncomplete}</div>
      ) : (
        <div>
          <p className={styles.introduction}>{es.introduction}</p>

          <Article title={es.articles.registrations.title}>
            <p>{es.articles.registrations.paragraph1}</p>
            <p>
              {es.articles.registrations.paragraph2(
                <a
                  href="https://arbeit.provinz.bz.it/de/uniurg"
                  target="_blank"
                >
                  {es.articles.registrations.paragraph2LinkLabel}
                </a>,
              )}
            </p>
            <p>
              {es.articles.registrations.paragraph3(
                <PayrollOfficeEmailAddress member={member} />,
              )}
            </p>
          </Article>

          <Article title={es.articles.disputes.title}>
            <p>{es.articles.disputes.paragraph1}</p>
            <p>{es.articles.disputes.paragraph2}</p>
          </Article>

          <Article title={es.articles.absence.title}>
            <p>
              {es.articles.absence.paragraph1}
              <br />
              {es.articles.absence.paragraph2}
              <br />
              {es.articles.absence.paragraph3}
              <br />
              {es.articles.absence.paragraph4}
              <br />
              {es.articles.absence.paragraph5}
              <br />
              {es.articles.absence.paragraph6}
              <br />
              {es.articles.absence.paragraph7}
              <br />
              {es.articles.absence.paragraph8}
            </p>
          </Article>

          <Article title={es.articles.workAccident.title}>
            <p>{es.articles.workAccident.paragraph}</p>
            <img
              className={styles.image}
              src={workAccidentCertificate}
              alt={es.articles.workAccident.certificateAltText}
            />
          </Article>

          <Article title={es.articles.illness.title}>
            <p>
              {es.articles.illness.paragraph1}
              <br />
              {es.articles.illness.paragraph2}
              <br />
              {es.articles.illness.paragraph3}
            </p>
            <p>{es.articles.illness.paragraph4}</p>
            <a
              href="https://serviziweb2.inps.it/AS0207/AttestatiMalCittadino/"
              target="_blank"
            >
              {es.articles.illness.inpsLinkLabel}
            </a>
            <img
              className={styles.image}
              src={sickNote}
              alt={es.articles.illness.sickNoteAltText}
            />
          </Article>

          <Article title={es.articles.individualService.title}>
            <p>{es.articles.individualService.paragraph1}</p>
            <p>{es.articles.individualService.paragraph2}</p>
            <p>{es.articles.individualService.paragraph3}</p>
            <p>{es.articles.individualService.summerHours}</p>
            <div>
              <strong>
                {es.articles.individualService.holidayHoursHeading}
              </strong>
              <ul className={styles.holidayList}>
                {es.articles.individualService.holidays.map((day) => (
                  <li key={day}>{day}</li>
                ))}
              </ul>
            </div>
            <p className={styles.warning}>
              <strong>{es.articles.individualService.warning}</strong>
            </p>
          </Article>

          <div className={styles.emergencyButton}>
            {isSuccess && (
              <Button
                type="primary"
                buttonProps={{
                  disabled: !status?.active,
                  title: !status?.active
                    ? es.serviceMomentarilyNotActive
                    : undefined,
                  onClick: () => setModalOpen(true),
                }}
              >
                {es.reportEmergency}
              </Button>
            )}
            {isValidating && <LoadingSpinner />}
            {error && <ErrorText text={es.initError} />}
          </div>

          <EmergencyModal
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
          />
        </div>
      )}
    </Page>
  );
};

type ArticleProps = PropsWithChildren & { title: string };

const Article = ({ title, children }: ArticleProps) => (
  <article className={styles.article}>
    <h2 className={styles.heading}>{title}</h2>
    {children}
  </article>
);

export default EmergencyService;
